import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
  issueAttributesType,
  issuesFetchParamsType,
  issuesSortOrderArrayType,
  issueWrapperArrayType,
  paginationPropType,
  pendingIssueFormAttributesType
} from '../../constants/proptype-validation';
import ProjectListContainer from './ProjectListContainer';
import IssueNewModalContainer from './IssueNewModalContainer';
import IssuePreviewModalContainer from './IssuePreviewModalContainer';
import IssueAttachmentGalleryContainer from '../issues/IssueAttachmentGalleryContainer';
import Button from '@adsk/bim360-matrix-react-button';
import ExportModal from '@adsk/bim360-matrix-react-export-modal';
import Loader from '@adsk/bim360-matrix-react-loader';
import TablePagination from '@adsk/bim360-matrix-react-table-pagination';
import Text from '@adsk/bim360-matrix-react-text';
import ControllableIFrameModalContainer from '../common/ControllableIFrameModalContainer';
import {getScrollableParent} from '../../utils/html-helpers';
import {getListReportDataObject, ReportsConfig} from '../../utils/reportHelpers';
import {toIsoDate} from '../../utils/date-helpers';
import {parseQueryString, stringifyQuery} from '../../utils/query-helpers';
import i18n from '../../utils/i18n';
import lodashGet from 'lodash/get';
import {PAGE_PARAMS} from '../../constants/issue-query-params';
import ProjectGridHeader from './ProjectGridHeader';
import ProjectListFilterPanelContainer from './ProjectListFilterPanelContainer';
import NoPermissionsModalContainer from '../common/NoPermissionsModalContainer';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {trimFileName} from '../../utils/notificationHelpers';
import {REPORT_TYPES} from '../../constants/ReportService';
import {reportFormats} from '../../constants/analytics';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {
  changeLocationAction,
  createReportAction,
  fetchActiveTemplatesAction,
  fetchIssuesAction,
  fetchIssueTemplateCategoriesAction,
  setPreviewIssueIdAction,
  setQueryParamsAction,
  trackEvent
} from '../../app/actions';
import {exportList} from '../../utils/analytics-helpers';
import {
  canCreateIssue as getCanCreateIssue,
  getAllIssueWrappersSorted,
  getCurrentPreviewIssueAttributes,
  getCurrentPreviewIssueId,
  getCurrentViewType,
  getFetchParams,
  getIsLoadingCurrentUserPermissions,
  getIssuesCollectionDidFetchOnce,
  getIssuesCollectionLoading,
  getIssuesCollectionPagination,
  getIssuesOrder,
  isCreatingReport as getIsCreatingReport,
  isShowNewIssueModal,
  showProjectViewCreateButton as getShouldShowProjectViewCreateButton
} from '../../app/selectors';
import {connect} from '../../app/connect';

class ProjectGrid extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isTableHeaderFixed: false,
      isPaginationFooterFixed: false,
      exportModalType: '',
      isFilterPanelOpen: false
    };

    this.handlePageScroll = this.handlePageScroll.bind(this);
    this.closePreviewModal = this.closePreviewModal.bind(this);
    this.toggleFilterPanel = this.toggleFilterPanel.bind(this);
    this.closeFilterPanel = this.closeFilterPanel.bind(this);

    this.modalTitles = {
      [reportFormats.XLSX]: i18n.t('export.exportXlsx'),
      [reportFormats.PDF]: i18n.t('export.exportPdf')
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handlePageScroll);
    this.props.dispatch(fetchIssuesAction({resetCollection: true}));
    this.props.dispatch(fetchIssueTemplateCategoriesAction());
    this.props.dispatch(fetchActiveTemplatesAction());
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isCreatingReport === false && this.props.isCreatingReport === true) {
      this.setState({exportModalType: ''});
    }

    if (!!this.props.issueId && !nextProps.issueId) {
      this.returnToListView();
    }
  }

  componentDidUpdate(prevProps) {
    const {fetchParams, dispatch, issues, loading} = this.props;
    // After we've fetched issues, find the first scrollable dom element and bind to it's scroll event (for hiding/showing sticky footer)
    // This is done in ComponentDidUpdate for making sure the Table is already rendered
    if (issues.length && !loading && !this.scrollableParent) {
      this.bindParentScrollEvent();
    }
    if (prevProps.fetchParams !== fetchParams) {
      dispatch(fetchIssuesAction({resetCollection: true}));
    }
  }

  componentWillUnmount() {
    if (this.scrollableParent) {
      this.scrollableParent.removeEventListener('scroll', this.handlePageScroll);
    }
    window.removeEventListener('resize', this.handlePageScroll);
  }

  bindParentScrollEvent() {
    this.scrollableParent = getScrollableParent(this.containerNode);

    if (this.scrollableParent) {
      this.scrollableParent.addEventListener('scroll', this.handlePageScroll);
    }
  }

  updateFixedElementsWidth() {
    const tableRowClass = '.MatrixTable';
    const tableHeaderRowClass = '.MatrixTable__header-row';
    const tableRow = this.tableElement.querySelector(tableRowClass);
    if (tableRow) {
      const rowWidth = tableRow.offsetWidth;
      const headerRow = this.tableElement.querySelector(tableHeaderRowClass);
      if (headerRow) {
        headerRow.style.width = `${rowWidth}px`;
      }
    }
  }

  closePreviewModal() {
    this.props.dispatch(setPreviewIssueIdAction(null));
  }

  toggleFilterPanel() {
    this.setState({isFilterPanelOpen: !this.state.isFilterPanelOpen});
  }

  closeFilterPanel() {
    this.setState({isFilterPanelOpen: false});
  }

  handlePageScroll() {
    if (this.scrollableParent && this.tableElement) {
      const TABLE_TOP_MINIMUM_MARGIN = 15;
      const currentScrollTop = this.scrollableParent.scrollTop;
      const pageClientRect = this.scrollableParent.getBoundingClientRect();
      const tableClientRect = this.tableElement.getBoundingClientRect();
      const isTableBottomHidden = tableClientRect.bottom > pageClientRect.bottom;

      // Show fixed pagination when scrolling up and table bottom is hidden or when the scroll is at the top
      const isPaginationFooterFixed =
        currentScrollTop < this.lastScrollPosition && isTableBottomHidden
          ? true
          : currentScrollTop > 0
            ? false
            : isTableBottomHidden;

      // Put the table header in fixed position when it reached the top of the page when scrolling
      const isTableHeaderFixed = tableClientRect.top - pageClientRect.top < TABLE_TOP_MINIMUM_MARGIN;

      this.lastScrollPosition = currentScrollTop;
      this.updateFixedElementsWidth();

      if (
        isPaginationFooterFixed !== this.state.isPaginationFooterFixed ||
        isTableHeaderFixed !== this.state.isTableHeaderFixed
      ) {
        this.setState({isPaginationFooterFixed, isTableHeaderFixed});
      }
    }
  }

  changeIssueDetailsModalId(issueId) {
    const {dispatch} = this.props;
    dispatch(changeLocationAction(`/project/${TYPE_QUALITY_ISSUES}/${issueId}`));
  }

  returnToListView() {
    const {dispatch} = this.props;
    dispatch(changeLocationAction(`/project/${TYPE_QUALITY_ISSUES}`));
  }

  handlePageChange(pageLink) {
    const isV2 = featureFlags.get(FEATURE_FLAGS.ISSUES_V2);
    let query = pageLink.substr(pageLink.indexOf('?') + 1);
    if (!isV2) {
      query = decodeURI(query);
    }
    const queryObject = parseQueryString(query);
    const offset = lodashGet(queryObject, isV2 ? PAGE_PARAMS.OFFSET : `page[${PAGE_PARAMS.OFFSET}]`);
    this.props.dispatch(setQueryParamsAction({offset: parseInt(offset, 10)}));
  }

  renderCreateBtn() {
    const {dispatch, canCreateIssue} = this.props;

    return (
      <Button
        styleType={Button.StyleType.PRIMARY}
        className="ProjectGrid__create-button"
        data-testid="ProjectGrid__createButton"
        onClick={() => {
          dispatch(changeLocationAction(`/project/${TYPE_QUALITY_ISSUES}/new`));
        }}
        disabled={!canCreateIssue}
      >
        {i18n.t('projectGrid.newBtn.title', {issueType: TYPE_QUALITY_ISSUES})}
      </Button>
    );
  }

  renderIssueNewModal() {
    const {showAddNewModal} = this.props;
    if (!showAddNewModal) {
      return null;
    }

    return (
      <IssueNewModalContainer container={this} onClose={this.returnToListView.bind(this)} isOpen allowCreateAnother />
    );
  }

  renderIssuePreviewModal() {
    const {issueId, issuesSortOrder} = this.props;
    const isModalOpen = !!issueId;

    return (
      <IssuePreviewModalContainer
        container={this}
        issueId={issueId}
        issuesSortOrder={issuesSortOrder}
        isModalOpen={isModalOpen}
        onClose={this.closePreviewModal}
        changePreviewId={this.changeIssueDetailsModalId.bind(this)}
      />
    );
  }

  renderExportModal(formatType) {
    const {dispatch, isCreatingReport, fetchParams, viewType} = this.props;
    const queryParams = {...fetchParams.queryParams, ...fetchParams.hiddenQueryParams};

    const textIssueType = i18n.t(['issue.label_capitalized', 'issueType.issues.label_capitalized']);
    const textReport = i18n.t('export.report');
    const textToday = toIsoDate(Date.now());

    return (
      <ExportModal
        show={this.state.exportModalType === formatType}
        container={this}
        modalTitle={this.modalTitles[formatType]}
        title={`${textIssueType} ${textReport} ${textToday}`}
        onCreate={({title, message}) => {
          const reportConfig = ReportsConfig[REPORT_TYPES.QUALITY_ISSUES];
          const fileType = formatType.toLocaleLowerCase();
          const reportData = getListReportDataObject(title, message, reportConfig, queryParams, fileType);
          const failureMessage = (
            <Text hasHTML>
              {trimFileName(i18n.t('notifications.failedToDownload', {fileName: `${title}.${fileType}`}))}
            </Text>
          );
          const reportMetadata = {fileType: formatType};
          dispatch(trackEvent(exportList(formatType, viewType)));
          dispatch(createReportAction(reportData, failureMessage, reportMetadata));
        }}
        onCancel={() => this.setState({exportModalType: ''})}
        onExited={() => this.setState({exportModalType: ''})}
        creating={isCreatingReport}
      />
    );
  }

  renderCollectionPaginationFooter() {
    const pagination = this.props.pagination ?? {};
    const {limit, offset, totalResults: total} = pagination;
    let {next: nextLink, previous: previousLink, first: firstLink, last: lastLink} = pagination;

    const workingLink = nextLink || previousLink;
    if (workingLink) {
      const {pathname: origin, search: queryString} = new URL(workingLink, window.location.origin);
      const query = parseQueryString(
        featureFlags.get(FEATURE_FLAGS.ISSUES_V2) ? encodeURIComponent(queryString) : queryString
      );
      const makeLink = newOffset =>
        `${origin}?${stringifyQuery({
          ...query,
          [featureFlags.get(FEATURE_FLAGS.ISSUES_V2) ? 'offset' : 'page[offset]']: newOffset
        })}`;

      if (!firstLink) {
        firstLink = makeLink(0);
      }
      if (!lastLink) {
        lastLink = makeLink(total - (total % limit));
      }
      if (!nextLink && offset < total - (total % limit)) {
        nextLink = makeLink(offset + limit);
      }
      if (!previousLink && offset >= limit) {
        previousLink = makeLink(offset - limit);
      }
    }

    const paginationLinks = {firstLink, lastLink, previousLink, nextLink};
    const paginationMeta = {limit, offset, total};
    const paginationClassNames = cx('ProjectGrid__pagination', {
      'ProjectGrid__pagination-footer-fixed': this.state.isPaginationFooterFixed
    });

    return (
      <TablePagination
        className={paginationClassNames}
        onPageChange={this.handlePageChange.bind(this)}
        customPaginationDescription={i18n.t('projectGrid.pagination.description')}
        paginationCounterSeparatorLabel={i18n.t('projectGrid.pagination.separatorLabel')}
        {...paginationMeta}
        {...paginationLinks}
      />
    );
  }

  renderTable() {
    const {issues, loading} = this.props;

    if (loading && !this.props.didFetchOnce) {
      return <Loader />;
    }

    const tableContainerClassNames = cx('ProjectGrid__table-container', {
      'ProjectGrid__table-header-fixed': this.state.isTableHeaderFixed
    });

    return (
      <div className={tableContainerClassNames}>
        {this.state.isTableHeaderFixed ? <div className="ProjectGrid__opaque-separator" /> : null}
        <div
          className="ProjectGrid__table-container-wrapper"
          ref={node => {
            this.tableElement = node;
          }}
        >
          <ProjectListFilterPanelContainer
            isFilterPanelOpen={this.state.isFilterPanelOpen}
            closeFilterPanel={this.closeFilterPanel}
            container={this}
          />

          <ProjectListContainer issues={issues}>{this.renderCollectionPaginationFooter()}</ProjectListContainer>
        </div>
      </div>
    );
  }

  render() {
    const {showProjectViewCreateButton} = this.props;

    return (
      <div
        className="ProjectGrid"
        ref={node => {
          this.containerNode = node;
        }}
      >
        {
          <ProjectGridHeader
            showExportModal={reportFormat => this.setState({exportModalType: reportFormat})}
            createBtn={showProjectViewCreateButton && this.renderCreateBtn()}
            toggleFilterPanel={this.toggleFilterPanel}
            isFilterPanelOpen={this.state.isFilterPanelOpen}
            container={this}
          />
        }

        {this.renderTable()}

        {this.renderIssueNewModal()}
        {this.renderIssuePreviewModal()}
        {this.renderExportModal(reportFormats.XLSX)}
        {this.renderExportModal(reportFormats.PDF)}
        <ControllableIFrameModalContainer />
        <IssueAttachmentGalleryContainer container={this} />
        <NoPermissionsModalContainer container={this} />
      </div>
    );
  }
}

ProjectGrid.displayName = 'ProjectGrid';

ProjectGrid.propTypes = {
  issues: issueWrapperArrayType.isRequired,
  issue: PropTypes.oneOfType([PropTypes.object, issueAttributesType, pendingIssueFormAttributesType]),
  loading: PropTypes.bool.isRequired,
  didFetchOnce: PropTypes.bool.isRequired,
  pagination: paginationPropType,
  issueId: PropTypes.string,
  showAddNewModal: PropTypes.bool,
  issuesSortOrder: issuesSortOrderArrayType,
  isCreatingReport: PropTypes.bool,
  fetchParams: issuesFetchParamsType.isRequired,
  showProjectViewCreateButton: PropTypes.bool.isRequired,
  isVoidedFilterOn: PropTypes.bool,
  canCreateIssue: PropTypes.bool.isRequired,
  isTemplateCategoriesCollectionDidFetchOnce: PropTypes.bool,
  viewType: PropTypes.string,
  dispatch: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  issues: getAllIssueWrappersSorted(state),
  issue: getCurrentPreviewIssueAttributes(state),
  loading: getIssuesCollectionLoading(state),
  didFetchOnce: getIssuesCollectionDidFetchOnce(state),
  pagination: getIssuesCollectionPagination(state),
  issueId: getCurrentPreviewIssueId(state),
  issuesSortOrder: getIssuesOrder(state),
  isCreatingReport: getIsCreatingReport(state),
  showAddNewModal: isShowNewIssueModal(state),
  isLoadingCurrentUserPermissions: getIsLoadingCurrentUserPermissions(state),
  fetchParams: getFetchParams(state),
  showProjectViewCreateButton: getShouldShowProjectViewCreateButton(state),
  canCreateIssue: getCanCreateIssue(state),
  viewType: getCurrentViewType(state)
});

export default connect(mapStateToProps)(ProjectGrid);
